import styled, { css } from 'styled-components';

// Colours and sizes from the approved design (JED-1486 Tutoring EOI canvas, boards 2–4).
const ink = '#1A1A1A';
const text = '#1E252A';
const body = '#405059';
const muted = '#5F5A66';
const primary = '#2E2277';
const border = '#CFCBD6';
const rule = '#ECE9F1';
const tint = '#F5F2FF';
const error = '#B42318';
const phone = '640px';

const font = css`
  font-family: var(
    --font-family-ibm_plex_sans,
    'IBM Plex Sans',
    Helvetica,
    sans-serif
  );
`;

// Above the mobile shell (5000) and the mobile upgrade layer (6000), below toasts (9999),
// like SchoolPlanEnquiry. The overlay scrolls, so the long form never needs an inner scroll.
export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 6100;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding: 40px 16px;
  box-sizing: border-box;
  background: rgba(37, 34, 42, 0.45);
  ${font};
  color: ${text};

  @media (max-width: ${phone}) {
    padding: 0;
    background: #fff;
  }
`;

export const Dialog = styled.div`
  position: relative;
  width: 100%;
  max-width: 720px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  outline: none;

  @media (max-width: ${phone}) {
    max-width: none;
    min-height: 100%;
    border-radius: 0;
    box-shadow: none;
    display: flex;
    flex-direction: column;
  }

  & *:focus-visible {
    outline: 3px solid #8e65e2;
    outline-offset: 2px;
  }
`;

const closeButton = css`
  width: 44px;
  height: 44px;
  border: none;
  border-radius: 22px;
  background: transparent;
  color: #56515b;
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  ${font};

  &:hover {
    background: rgba(46, 34, 119, 0.08);
  }
`;

// The close button's home. Desktop: pinned to the dialog's top-right corner, over the hero. Phone: the
// sheet's sticky top bar, with the "Tutoring" eyebrow. One close button, so focus always lands on it.
export const TopBar = styled.div`
  position: absolute;
  top: 14px;
  right: 14px;
  z-index: 2;

  > span {
    display: none;
  }

  @media (max-width: ${phone}) {
    position: sticky;
    top: 0;
    right: auto;
    height: 56px;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 6px 0 16px;
    background: #fff;
    border-bottom: 1px solid ${rule};

    > span {
      display: inline;
    }
  }
`;

export const CloseButton = styled.button`
  ${closeButton};
`;

export const Eyebrow = styled.div`
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${primary};
`;

export const Hero = styled.div`
  position: relative;
  padding: 36px 32px 24px;
  border-radius: 16px 16px 0 0;
  background: linear-gradient(180deg, ${tint} 0%, #eae1ff 100%);

  @media (max-width: ${phone}) {
    padding: 20px 16px 16px;
    border-radius: 0;
    background: linear-gradient(180deg, ${tint} 0%, #fff 100%);

    ${Eyebrow} {
      display: none;
    }
  }
`;

export const Title = styled.h2`
  margin: 10px 0 8px;
  font-size: 30px;
  font-weight: 700;
  line-height: 1.25;
  color: ${ink};

  @media (max-width: ${phone}) {
    margin-top: 0;
    font-size: 24px;
  }
`;

export const Lead = styled.p`
  margin: 0;
  max-width: 560px;
  font-size: 16px;
  line-height: 1.6;
  color: ${body};

  @media (max-width: ${phone}) {
    font-size: 15px;
    line-height: 1.55;
  }
`;

export const Form = styled.form`
  padding: 28px 32px 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;

  @media (max-width: ${phone}) {
    flex-grow: 1;
    padding: 8px 16px 0;
    gap: 28px;
  }
`;

export const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: ${(p) => (p.$gap ? `${p.$gap}px` : '16px')};

  & + & {
    padding-top: 28px;
    border-top: 1px solid ${rule};
  }
`;

export const SectionTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;

  @media (max-width: ${phone}) {
    font-size: 17px;
  }
`;

export const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;

  @media (max-width: ${phone}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 14px;
  }
`;

export const Pair = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const Label = styled.label`
  font-size: 14px;
  font-weight: 600;
`;

export const Optional = styled.span`
  font-weight: 400;
  color: ${muted};
`;

const control = css`
  box-sizing: border-box;
  width: 100%;
  border: 1px solid ${(p) => (p.$invalid ? error : border)};
  border-radius: 8px;
  background: #fff;
  ${font};
  font-size: 15px;
  color: ${text};

  @media (max-width: ${phone}) {
    font-size: 16px;
  }
`;

export const Input = styled.input`
  ${control};
  height: 44px;
  padding: 0 12px;
`;

export const Select = styled.select`
  ${control};
  height: 44px;
  padding: 0 8px;
`;

export const Textarea = styled.textarea`
  ${control};
  padding: 10px 12px;
  line-height: 1.5;
  resize: vertical;
`;

export const Hint = styled.span`
  font-size: 13px;
  color: ${muted};
`;

export const CheckRow = styled.label`
  display: flex;
  align-items: ${(p) => (p.$top ? 'flex-start' : 'center')};
  gap: ${(p) => (p.$top ? '12px' : '10px')};
  min-height: 44px;
  font-size: 15px;
  line-height: 1.5;
  cursor: pointer;

  input {
    width: 20px;
    height: 20px;
    margin: ${(p) => (p.$top ? '2px 0 0' : '0')};
    flex-shrink: 0;
    accent-color: ${primary};
  }

  a {
    color: ${primary};
  }
`;

export const ParentBox = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  padding: 16px;
  background: #f7f5fb;
  border-radius: 10px;

  @media (max-width: ${phone}) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

export const Fieldset = styled.fieldset`
  margin: 0;
  padding: 0;
  border: none;
  display: flex;
  flex-direction: column;
  gap: 10px;
  min-width: 0;
`;

export const Legend = styled.legend`
  padding: 0;
  margin-bottom: 10px;
  font-size: 14px;
  font-weight: 600;
`;

export const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const Chip = styled.button`
  height: 44px;
  padding: 0 16px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 22px;
  border: 1px solid ${(p) => (p['aria-pressed'] ? primary : border)};
  background: ${(p) => (p['aria-pressed'] ? primary : '#fff')};
  color: ${(p) => (p['aria-pressed'] ? '#fff' : text)};
  ${font};
  font-size: 15px;
  font-weight: 500;
  cursor: pointer;

  @media (max-width: ${phone}) {
    padding: 0 14px;
  }
`;

export const Options = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(170px, 1fr));
  gap: 12px;

  @media (max-width: ${phone}) {
    grid-template-columns: minmax(0, 1fr);
    gap: 10px;
  }
`;

export const OptionCard = styled.button`
  position: relative;
  min-height: 92px;
  padding: ${(p) => (p['aria-pressed'] ? '13px 15px' : '14px 16px')};
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  text-align: left;
  border-radius: 10px;
  border: ${(p) =>
    p['aria-pressed'] ? `2px solid ${primary}` : `1px solid ${border}`};
  background: ${(p) => (p['aria-pressed'] ? tint : '#fff')};
  ${font};
  color: ${text};
  cursor: pointer;

  @media (max-width: ${phone}) {
    min-height: 60px;
    flex-direction: row;
    align-items: center;
    gap: 12px;
    padding-top: 0;
    padding-bottom: 0;
  }
`;

export const OptionText = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;

  @media (max-width: ${phone}) {
    flex-grow: 1;
    gap: 0;
  }
`;

export const OptionTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
`;

export const OptionHint = styled.span`
  font-size: 13px;
  color: ${muted};
`;

export const OptionCheck = styled.span`
  position: absolute;
  top: 12px;
  right: 12px;
  width: 22px;
  height: 22px;
  border-radius: 11px;
  background: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;

  @media (max-width: ${phone}) {
    position: static;
    flex-shrink: 0;
  }
`;

export const FieldError = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${error};
`;

export const Footer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: -16px;

  @media (max-width: ${phone}) {
    margin-top: 0;
    position: sticky;
    bottom: 0;
    z-index: 2;
    margin: 0 -16px;
    padding: 12px 16px 24px;
    gap: 8px;
    background: #fff;
    border-top: 1px solid ${rule};
  }
`;

export const Actions = styled.div`
  display: flex;
  flex-wrap: wrap-reverse;
  justify-content: flex-end;
  align-items: center;
  gap: 12px;
`;

export const CancelButton = styled.button`
  height: 48px;
  padding: 0 20px;
  border: none;
  background: transparent;
  color: ${primary};
  ${font};
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;

  @media (max-width: ${phone}) {
    display: none;
  }
`;

export const SubmitButton = styled.button`
  height: 48px;
  padding: 0 28px;
  border: 2px solid ${primary};
  background: ${primary};
  color: #fff;
  border-radius: 6px;
  ${font};
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;

  &:disabled {
    opacity: 0.7;
    cursor: progress;
  }

  @media (max-width: ${phone}) {
    width: 100%;
    height: 52px;
    border-radius: 8px;
  }
`;

export const Note = styled.p`
  margin: 0;
  text-align: right;
  font-size: 13px;
  color: ${muted};

  .phone {
    display: none;
  }

  @media (max-width: ${phone}) {
    text-align: center;

    .desktop {
      display: none;
    }
    .phone {
      display: inline;
    }
  }
`;

export const Sent = styled.div`
  padding: 56px 40px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  text-align: center;
  border-radius: 16px;
  background: linear-gradient(180deg, ${tint} 0%, #fff 60%);

  @media (max-width: ${phone}) {
    flex-grow: 1;
    padding: 40px 16px 32px;
    border-radius: 0;
  }
`;

export const SuccessIcon = styled.span`
  width: 64px;
  height: 64px;
  border-radius: 32px;
  background: ${primary};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const SentTitle = styled.h2`
  margin: 8px 0 0;
  font-size: 28px;
  font-weight: 700;
  color: ${ink};

  @media (max-width: ${phone}) {
    font-size: 24px;
  }
`;

export const SentText = styled.p`
  margin: 0;
  max-width: 480px;
  font-size: 16px;
  line-height: 1.6;
  color: ${body};
  overflow-wrap: anywhere;
`;

export const SummaryChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 8px;
  margin-top: 4px;
`;

export const SummaryChip = styled.span`
  padding: 6px 12px;
  border-radius: 999px;
  background: #f0e7ff;
  color: ${primary};
  font-size: 14px;
  font-weight: 500;
`;

export const BackButton = styled.button`
  margin-top: 16px;
  height: 48px;
  padding: 0 28px;
  border: none;
  background: ${primary};
  color: #fff;
  border-radius: 6px;
  ${font};
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
`;

export const SmallNote = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${muted};
`;
